/**
 * SliderControls - Control buttons for slider/progress bar sections
 *
 * Provides:
 * - Play/Pause button for preview animation (toggles between play and pause states)
 * - Add Item button for creating new slides
 * - Combined controls component for backward compatibility
 *
 * Pattern follows ListSection header controls
 */

import React, { memo } from 'react';
import { Box, Button, Tooltip } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import AddIcon from '@mui/icons-material/Add';
import type { SliderControlsProps } from '../../../../types/sliderSection.types';

/**
 * Props for PlayPauseButton (renamed from PlayStopButton)
 */
export interface PlayPauseButtonProps {
  /** Called when play is clicked (start or resume animation) */
  onPlay: () => void;
  /** Called when pause is clicked (freeze animation at current position) */
  onPause: () => void;
  /** Whether animation is currently playing */
  isPlaying: boolean;
  /** Whether the button is disabled */
  disabled: boolean;
}

/**
 * PlayPauseButton - Toggle between Play and Pause states
 *
 * Unlike the previous PlayStopButton:
 * - Pause freezes at current progress (can resume)
 * - No "stop" that resets to 0%
 *
 * @example
 * <PlayPauseButton
 *   onPlay={handlePlay}
 *   onPause={handlePause}
 *   isPlaying={animationState.isPlaying}
 *   disabled={false}
 * />
 */
export const PlayPauseButton: React.FC<PlayPauseButtonProps> = memo(function PlayPauseButton({
  onPlay,
  onPause,
  isPlaying,
  disabled,
}) {
  const handleClick = () => {
    if (isPlaying) {
      onPause();
    } else {
      onPlay();
    }
  };

  return (
    <Tooltip title={isPlaying ? 'Pause preview' : 'Play preview'}>
      <span>
        <Button
          data-testid="slider-play-button"
          variant={isPlaying ? 'contained' : 'outlined'}
          color="primary"
          size="small"
          onClick={handleClick}
          disabled={disabled}
          startIcon={isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          sx={{ minWidth: 100 }}
        >
          {isPlaying ? 'Pause' : 'Play'}
        </Button>
      </span>
    </Tooltip>
  );
});

/**
 * @deprecated Use PlayPauseButton instead
 * Kept for backwards compatibility - wraps PlayPauseButton with old prop names
 */
export interface PlayStopButtonProps {
  onPlay: () => void;
  onStop: () => void;
  isPlaying: boolean;
  disabled: boolean;
}

/**
 * @deprecated Use PlayPauseButton instead
 */
export const PlayStopButton: React.FC<PlayStopButtonProps> = memo(function PlayStopButton({
  onPlay,
  onStop,
  isPlaying,
  disabled,
}) {
  // Map old onStop to new onPause
  return (
    <PlayPauseButton
      onPlay={onPlay}
      onPause={onStop}
      isPlaying={isPlaying}
      disabled={disabled}
    />
  );
});

/**
 * Props for AddSlideButton
 */
export interface AddSlideButtonProps {
  onAdd: () => void;
  canAddMore: boolean;
  disabled: boolean;
}

/**
 * AddSlideButton - Standalone add slide button
 */
export const AddSlideButton: React.FC<AddSlideButtonProps> = memo(function AddSlideButton({
  onAdd,
  canAddMore,
  disabled,
}) {
  return (
    <Tooltip title={canAddMore ? 'Add new slide' : 'Maximum slides reached'}>
      <span>
        <Button
          data-testid="slider-add-item-button"
          variant="outlined"
          size="small"
          onClick={onAdd}
          disabled={disabled || !canAddMore}
          startIcon={<AddIcon />}
        >
          Add Slide
        </Button>
      </span>
    </Tooltip>
  );
});

/**
 * SliderControls Component (combined - for backward compatibility)
 *
 * Layout: [Play/Pause Button] [Add Item Button]
 */
export const SliderControls: React.FC<SliderControlsProps> = memo(function SliderControls({
  onPlay,
  onStop,
  onAdd,
  isPlaying,
  canAddMore,
  disabled,
}) {
  return (
    <Box
      data-testid="slider-controls"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 2,
      }}
    >
      <PlayPauseButton
        onPlay={onPlay}
        onPause={onStop}
        isPlaying={isPlaying}
        disabled={disabled}
      />
      <AddSlideButton
        onAdd={onAdd}
        canAddMore={canAddMore}
        disabled={disabled}
      />
    </Box>
  );
});

export default SliderControls;
